/**
 * Framework Section Configurations
 * 
 * Defines how each framework type's SoA is divided into logical sections.
 * Each framework type (standard, regulation, law, benchmark, custom) has
 * different sections based on its inherent structure.
 */

import { FrameworkLayoutConfig, FrameworkSectionConfig, GRCControl } from '../types/grc';

// ─── HELPER MATCHERS ────────────────────────────────────────
// Each matcher first checks section_type from AI/backend, then falls back to keyword matching

/** Matches ISO Annex A controls (A.x.x.x pattern) */
const isAnnexA = (c: GRCControl) => c.section_type === 'annex_control' || /^A\.\d/.test(c.control_code || '');

/** Matches ISO management clauses (4-10) */
const isManagementClause = (c: GRCControl) => {
  if (c.section_type === 'management_clause') return true;
  const code = c.control_code || '';
  return !isAnnexA(c) && /^\d/.test(code);
};

/** Matches penalty/fine items based on section_type or keywords/chapter markers */
const isPenalty = (c: GRCControl) => {
  if (c.section_type === 'penalty') return true;
  const text = `${c.title} ${c.description}`.toLowerCase();
  return text.includes('kazn') || text.includes('penalt') || text.includes('fine') ||
         text.includes('sankcij') || text.includes('sanction') || text.includes('prekršaj') ||
         text.includes('novčan') || text.includes('zatvor') || text.includes('imprisonment') ||
         (c.chapter_title || '').toLowerCase().includes('kazn') ||
         (c.chapter_title || '').toLowerCase().includes('penalt');
};

/** Matches state/government obligation items */
const isStateObligation = (c: GRCControl) => {
  if (c.section_type === 'obligation_state') return true;
  const text = `${c.title} ${c.description} ${c.chapter_title || ''}`.toLowerCase();
  return (text.includes('nadležni organ') || text.includes('competent authority') ||
          text.includes('poverenik') || text.includes('commissioner') ||
          text.includes('regulatorno') || text.includes('regulatory') ||
          text.includes('nadzor') || text.includes('supervision') ||
          text.includes('obavez') && (text.includes('držav') || text.includes('organ') || text.includes('state')));
};

/** Matches company/entity obligation items */
const isCompanyObligation = (c: GRCControl) => {
  if (c.section_type === 'obligation_company') return true;
  const text = `${c.title} ${c.description} ${c.chapter_title || ''}`.toLowerCase();
  return (text.includes('rukovalac') || text.includes('controller') ||
          text.includes('obrađivač') || text.includes('processor') ||
          text.includes('obavez') && (text.includes('kompanij') || text.includes('organizacij') || text.includes('company')) ||
          text.includes('obligation') && (text.includes('company') || text.includes('organization')));
};

/** Matches rights (data subject, citizens, etc.) */
const isRight = (c: GRCControl) => {
  if (c.section_type === 'right') return true;
  const text = `${c.title} ${c.description} ${c.chapter_title || ''}`.toLowerCase();
  return text.includes('prav') && (text.includes('lice') || text.includes('subject') || text.includes('korisnik')) ||
         text.includes('right') && (text.includes('subject') || text.includes('individual') || text.includes('citizen')) ||
         text.includes('pristup') && text.includes('podatk') ||
         text.includes('access') && text.includes('data') ||
         text.includes('brisanje') || text.includes('erasure') || text.includes('portabilnost') || text.includes('portability');
};

/** Matches definitions/general provisions */
const isDefinition = (c: GRCControl) => {
  if (c.section_type === 'definition') return true;
  const text = `${c.title} ${c.description}`.toLowerCase();
  const code = c.control_code || '';
  return (text.includes('definicij') || text.includes('definition') ||
          text.includes('pojmov') || text.includes('term') ||
          text.includes('značenj') || text.includes('meaning')) &&
         !isPenalty(c) && (parseInt(code) <= 3 || code.includes('čl.1') || code.includes('čl.2') || code.includes('čl.3'));
};

/** Matches general provisions (scope, purpose, etc.) */
const isProvision = (c: GRCControl) => {
  if (c.section_type === 'provision') return true;
  const text = `${c.title} ${c.description} ${c.chapter_title || ''}`.toLowerCase();
  return (text.includes('predmet') || text.includes('subject matter') ||
          text.includes('cilj') || text.includes('objective') || text.includes('purpose') ||
          text.includes('scope') || text.includes('primena') || text.includes('application') ||
          text.includes('opšte odredbe') || text.includes('general provision') ||
          text.includes('uvod') || text.includes('introduction') ||
          text.includes('osnov') || text.includes('basic') || text.includes('princip') || text.includes('principle')) &&
         !isPenalty(c) && !isStateObligation(c) && !isCompanyObligation(c);
};

/** Matches procedures (required processes) */
const isProcedure = (c: GRCControl) => {
  if (c.section_type === 'procedure') return true;
  const text = `${c.title} ${c.description}`.toLowerCase();
  return text.includes('procedur') || text.includes('process') || text.includes('postupak') ||
         text.includes('evidencij') || text.includes('record') ||
         text.includes('obaveštenj') || text.includes('notificat');
};

/** CIS automated check */
const isAutomatedCheck = (c: GRCControl) => c.section_type === 'automated_check' || c.check_type === 'automated';
/** CIS manual check */
const isManualCheck = (c: GRCControl) => c.section_type === 'manual_check' || c.check_type === 'manual' || c.check_type === 'hybrid';

// ─── PCI DSS MATCHERS ────────────────────────────────────────

/** PCI DSS Requirement 1-2: Build and Maintain a Secure Network */
const isPciNetworkSecurity = (c: GRCControl) => {
  const code = c.control_code || '';
  return /^(1\.|2\.)/.test(code);
};

/** PCI DSS Requirement 3-4: Protect Account Data */
const isPciDataProtection = (c: GRCControl) => {
  const code = c.control_code || '';
  return /^(3\.|4\.)/.test(code);
};

/** PCI DSS Requirement 5-6: Vulnerability Management */
const isPciVulnManagement = (c: GRCControl) => {
  const code = c.control_code || '';
  return /^(5\.|6\.)/.test(code);
};

/** PCI DSS Requirement 7-9: Access Control */
const isPciAccessControl = (c: GRCControl) => {
  const code = c.control_code || '';
  return /^(7\.|8\.|9\.)/.test(code);
};

/** PCI DSS Requirement 10-11: Monitoring and Testing */
const isPciMonitoringTesting = (c: GRCControl) => {
  const code = c.control_code || '';
  return /^(10\.|11\.)/.test(code);
};

/** PCI DSS Requirement 12: Security Policies */
const isPciSecurityPolicies = (c: GRCControl) => {
  const code = c.control_code || '';
  return /^12\./.test(code);
};

/** General compliance requirement (fallback) */
const isRequirement = (c: GRCControl) =>
  c.section_type === 'requirement' ||
  (!isPenalty(c) && !isDefinition(c) && !isProvision(c) &&
  !isStateObligation(c) && !isRight(c));

// ─── SECTION TEMPLATES ──────────────────────────────────────

const standardSections: FrameworkSectionConfig[] = [
  {
    id: 'annex-controls',
    label: 'Annex A Controls',
    icon: 'Shield',
    type: 'annex_controls',
    description: 'Implementation controls from Annex A',
    showApplicability: true,
    showEvidence: true,
    showFulfillment: true,
    showPenaltyLink: false,
    showPolicyLink: true,
    readOnly: false,
    filterFn: isAnnexA,
  },
  {
    id: 'management-clauses',
    label: 'Management Clauses (4-10)',
    icon: 'BookOpen',
    type: 'management_clauses',
    description: 'ISMS management system requirements',
    showApplicability: true,
    showEvidence: true,
    showFulfillment: true,
    showPenaltyLink: false,
    showPolicyLink: true,
    readOnly: false,
    filterFn: isManagementClause,
  },
];

const lawSections: FrameworkSectionConfig[] = [
  {
    id: 'provisions',
    label: 'General Provisions',
    icon: 'BookOpen',
    type: 'provisions',
    description: 'Scope, purpose, and foundational principles',
    showApplicability: false,
    showEvidence: false,
    showFulfillment: false,
    showPenaltyLink: false,
    showPolicyLink: false,
    readOnly: true,
    filterFn: isProvision,
  },
  {
    id: 'definitions',
    label: 'Definitions & Terms',
    icon: 'FileText',
    type: 'definitions',
    description: 'Key terms and definitions used in this law',
    showApplicability: false,
    showEvidence: false,
    showFulfillment: false,
    showPenaltyLink: false,
    showPolicyLink: false,
    readOnly: true,
    filterFn: isDefinition,
  },
  {
    id: 'requirements',
    label: 'Compliance Requirements',
    icon: 'ClipboardCheck',
    type: 'requirements',
    description: 'Requirements your organization must comply with',
    showApplicability: true,
    showEvidence: true,
    showFulfillment: true,
    showPenaltyLink: true,
    showPolicyLink: true,
    readOnly: false,
    filterFn: (c) => isCompanyObligation(c) || (isRequirement(c) && !isProcedure(c) && !isRight(c) && !isManualCheck(c) && !isAutomatedCheck(c) && !isProvision(c) && !isDefinition(c)),
  },
  {
    id: 'rights',
    label: 'Rights of Data Subjects',
    icon: 'Users',
    type: 'rights',
    description: 'Rights that must be honored and facilitated',
    showApplicability: true,
    showEvidence: true,
    showFulfillment: true,
    showPenaltyLink: true,
    showPolicyLink: true,
    readOnly: false,
    filterFn: isRight,
  },
  {
    id: 'obligations-state',
    label: 'State / Authority Obligations',
    icon: 'Landmark',
    type: 'obligations_state',
    description: 'Obligations of regulatory bodies—informational for your awareness',
    showApplicability: false,
    showEvidence: false,
    showFulfillment: false,
    showPenaltyLink: false,
    showPolicyLink: false,
    readOnly: true,
    filterFn: isStateObligation,
  },
  {
    id: 'procedures',
    label: 'Required Procedures',
    icon: 'ListChecks',
    type: 'procedures',
    description: 'Procedures and notification requirements',
    showApplicability: true,
    showEvidence: true,
    showFulfillment: true,
    showPenaltyLink: true,
    showPolicyLink: true,
    readOnly: false,
    filterFn: isProcedure,
  },
  {
    id: 'penalties',
    label: 'Penalties & Sanctions',
    icon: 'AlertTriangle',
    type: 'penalties',
    description: 'Fines and penalties for non-compliance—linked to relevant controls',
    showApplicability: false,
    showEvidence: false,
    showFulfillment: false,
    showPenaltyLink: false,
    showPolicyLink: false,
    readOnly: true,
    filterFn: isPenalty,
  },
];

const regulationSections: FrameworkSectionConfig[] = [
  {
    id: 'provisions',
    label: 'General Provisions',
    icon: 'BookOpen',
    type: 'provisions',
    description: 'Scope and foundational principles of the regulation',
    showApplicability: false,
    showEvidence: false,
    showFulfillment: false,
    showPenaltyLink: false,
    showPolicyLink: false,
    readOnly: true,
    filterFn: isProvision,
  },
  {
    id: 'requirements',
    label: 'Compliance Requirements',
    icon: 'ClipboardCheck',
    type: 'requirements',
    description: 'Requirements your organization must comply with',
    showApplicability: true,
    showEvidence: true,
    showFulfillment: true,
    showPenaltyLink: true,
    showPolicyLink: true,
    readOnly: false,
    filterFn: (c) => isRequirement(c) && !isProcedure(c) && !isRight(c) && !isProvision(c) && !isDefinition(c),
  },
  {
    id: 'obligations-company',
    label: 'Entity Obligations',
    icon: 'Building2',
    type: 'obligations_company',
    description: 'Specific obligations for regulated entities',
    showApplicability: true,
    showEvidence: true,
    showFulfillment: true,
    showPenaltyLink: true,
    showPolicyLink: true,
    readOnly: false,
    filterFn: isCompanyObligation,
  },
  {
    id: 'obligations-state',
    label: 'Authority Obligations',
    icon: 'Landmark',
    type: 'obligations_state',
    description: 'Obligations of supervisory authorities—informational',
    showApplicability: false,
    showEvidence: false,
    showFulfillment: false,
    showPenaltyLink: false,
    showPolicyLink: false,
    readOnly: true,
    filterFn: isStateObligation,
  },
  {
    id: 'penalties',
    label: 'Penalties & Fines',
    icon: 'AlertTriangle',
    type: 'penalties',
    description: 'Administrative fines and sanctions',
    showApplicability: false,
    showEvidence: false,
    showFulfillment: false,
    showPenaltyLink: false,
    showPolicyLink: false,
    readOnly: true,
    filterFn: isPenalty,
  },
];

const benchmarkSections: FrameworkSectionConfig[] = [
  {
    id: 'automated-checks',
    label: 'Automated Checks',
    icon: 'Cpu',
    type: 'automated_checks',
    description: 'Controls verifiable through automated scanning',
    showApplicability: true,
    showEvidence: true,
    showFulfillment: true,
    showPenaltyLink: false,
    showPolicyLink: false,
    readOnly: false,
    filterFn: isAutomatedCheck,
  },
  {
    id: 'manual-checks',
    label: 'Manual Checks',
    icon: 'ClipboardList',
    type: 'manual_checks',
    description: 'Controls requiring manual verification',
    showApplicability: true,
    showEvidence: true,
    showFulfillment: true,
    showPenaltyLink: false,
    showPolicyLink: false,
    readOnly: false,
    filterFn: isManualCheck,
  },
];

const customSections: FrameworkSectionConfig[] = [
  {
    id: 'all-controls',
    label: 'All Controls',
    icon: 'List',
    type: 'requirements',
    description: 'All controls and requirements',
    showApplicability: true,
    showEvidence: true,
    showFulfillment: true,
    showPenaltyLink: false,
    showPolicyLink: true,
    readOnly: false,
    filterFn: () => true,
  },
];

// ─── PCI DSS SECTIONS ────────────────────────────────────────

const pciDssSections: FrameworkSectionConfig[] = [
  {
    id: 'network-security',
    label: 'Network Security (Req 1-2)',
    icon: 'Shield',
    type: 'requirements',
    description: 'Build and Maintain a Secure Network and Systems',
    showApplicability: true,
    showEvidence: true,
    showFulfillment: true,
    showPenaltyLink: false,
    showPolicyLink: true,
    readOnly: false,
    filterFn: isPciNetworkSecurity,
  },
  {
    id: 'data-protection',
    label: 'Data Protection (Req 3-4)',
    icon: 'Lock',
    type: 'requirements',
    description: 'Protect Account Data',
    showApplicability: true,
    showEvidence: true,
    showFulfillment: true,
    showPenaltyLink: false,
    showPolicyLink: true,
    readOnly: false,
    filterFn: isPciDataProtection,
  },
  {
    id: 'vuln-management',
    label: 'Vulnerability Mgmt (Req 5-6)',
    icon: 'Bug',
    type: 'requirements',
    description: 'Maintain a Vulnerability Management Program',
    showApplicability: true,
    showEvidence: true,
    showFulfillment: true,
    showPenaltyLink: false,
    showPolicyLink: true,
    readOnly: false,
    filterFn: isPciVulnManagement,
  },
  {
    id: 'access-control',
    label: 'Access Control (Req 7-9)',
    icon: 'Users',
    type: 'requirements',
    description: 'Implement Strong Access Control Measures',
    showApplicability: true,
    showEvidence: true,
    showFulfillment: true,
    showPenaltyLink: false,
    showPolicyLink: true,
    readOnly: false,
    filterFn: isPciAccessControl,
  },
  {
    id: 'monitoring-testing',
    label: 'Monitoring & Testing (Req 10-11)',
    icon: 'Activity',
    type: 'requirements',
    description: 'Regularly Monitor and Test Networks',
    showApplicability: true,
    showEvidence: true,
    showFulfillment: true,
    showPenaltyLink: false,
    showPolicyLink: true,
    readOnly: false,
    filterFn: isPciMonitoringTesting,
  },
  {
    id: 'security-policies',
    label: 'Security Policies (Req 12)',
    icon: 'FileText',
    type: 'requirements',
    description: 'Maintain an Information Security Policy',
    showApplicability: true,
    showEvidence: true,
    showFulfillment: true,
    showPenaltyLink: false,
    showPolicyLink: true,
    readOnly: false,
    filterFn: isPciSecurityPolicies,
  },
];

// ─── LAYOUTS ─────────────────────────────────────────────────

const layouts: FrameworkLayoutConfig[] = [
  {
    frameworkType: 'standard',
    sections: standardSections,
    exportSheetNames: {
      'annex-controls': 'Annex A Controls',
      'management-clauses': 'Management Clauses',
    },
  },
  {
    frameworkType: 'law',
    sections: lawSections,
    exportSheetNames: {
      'provisions': 'General Provisions',
      'definitions': 'Definitions',
      'requirements': 'Compliance Requirements',
      'rights': 'Rights',
      'obligations-state': 'State Obligations',
      'procedures': 'Required Procedures',
      'penalties': 'Penalties & Sanctions',
    },
  },
  {
    frameworkType: 'regulation',
    sections: regulationSections,
    exportSheetNames: {
      'provisions': 'General Provisions',
      'requirements': 'Compliance Requirements',
      'obligations-company': 'Entity Obligations',
      'obligations-state': 'Authority Obligations',
      'penalties': 'Penalties & Fines',
    },
  },
  {
    frameworkType: 'benchmark',
    sections: benchmarkSections,
    exportSheetNames: {
      'automated-checks': 'Automated Checks',
      'manual-checks': 'Manual Checks',
    },
  },
  {
    frameworkType: 'custom',
    sections: customSections,
    exportSheetNames: {
      'all-controls': 'All Controls',
    },
  },
];

// Framework code to specific layout mapping (overrides framework_type)
const frameworkCodeOverrides: Record<string, FrameworkLayoutConfig> = {
  'PCIDSS': {
    frameworkType: 'standard',
    sections: pciDssSections,
    exportSheetNames: {
      'network-security': 'Network Security',
      'data-protection': 'Data Protection',
      'vuln-management': 'Vulnerability Management',
      'access-control': 'Access Control',
      'monitoring-testing': 'Monitoring & Testing',
      'security-policies': 'Security Policies',
    },
  },
};

// ─── PUBLIC API ──────────────────────────────────────────────

/**
 * Get the SoA section layout for a given framework.
 * Matches by framework_type, with optional code override for special cases.
 */
export function getFrameworkLayout(frameworkType: string, frameworkCode?: string): FrameworkLayoutConfig {
  // Check for specific code overrides first (e.g., PCI DSS has custom sections)
  if (frameworkCode && frameworkCodeOverrides[frameworkCode]) {
    return frameworkCodeOverrides[frameworkCode];
  }
  
  const matched = layouts.find(l => l.frameworkType === frameworkType);
  return matched || layouts.find(l => l.frameworkType === 'custom')!;
}

/**
 * Categorize controls into sections based on the layout configuration.
 * Controls that don't match any section filter go into an "Other" bucket.
 */
export function categorizeControls(
  controls: GRCControl[],
  layout: FrameworkLayoutConfig
): Record<string, GRCControl[]> {
  const result: Record<string, GRCControl[]> = {};
  const assigned = new Set<number>();

  for (const section of layout.sections) {
    result[section.id] = [];
  }

  // First pass: assign controls with section_override
  for (const control of controls) {
    if (control.section_override) {
      const targetSection = layout.sections.find(s => s.id === control.section_override);
      if (targetSection) {
        result[targetSection.id].push(control);
        assigned.add(control.id);
      }
    }
  }

  // Second pass: assign remaining controls using filterFn
  for (const section of layout.sections) {
    for (const control of controls) {
      if (!assigned.has(control.id) && section.filterFn(control)) {
        result[section.id].push(control);
        assigned.add(control.id);
      }
    }
  }

  // Uncategorized controls go into first non-readOnly section or a special bucket
  const uncategorized = controls.filter(c => !assigned.has(c.id));
  if (uncategorized.length > 0) {
    const primarySection = layout.sections.find(s => !s.readOnly);
    if (primarySection) {
      result[primarySection.id] = [...(result[primarySection.id] || []), ...uncategorized];
    } else {
      result['_uncategorized'] = uncategorized;
    }
  }

  return result;
}

/**
 * Build penalty-to-control links.
 * Returns a map of penalty control IDs to arrays of related requirement control IDs.
 */
export function buildPenaltyLinks(
  penaltyControls: GRCControl[],
  requirementControls: GRCControl[]
): Map<number, number[]> {
  const links = new Map<number, number[]>();

  for (const penalty of penaltyControls) {
    const penaltyText = `${penalty.title} ${penalty.description}`.toLowerCase();
    const relatedReqs: number[] = [];

    for (const req of requirementControls) {
      // Match by article code reference
      if (req.article_code && penaltyText.includes(req.article_code.toLowerCase())) {
        relatedReqs.push(req.id);
        continue;
      }
      // Match by control code reference
      if (req.control_code && penaltyText.includes(req.control_code.toLowerCase())) {
        relatedReqs.push(req.id);
        continue;
      }
      // Match by title keywords
      const reqKeywords = req.title.toLowerCase().split(/\s+/).filter(w => w.length > 4);
      const matchCount = reqKeywords.filter(kw => penaltyText.includes(kw)).length;
      if (matchCount >= 2) {
        relatedReqs.push(req.id);
      }
    }

    links.set(penalty.id, relatedReqs);
  }

  return links;
}
